// @flow

import * as React from 'react';
import { createFragmentContainer, graphql } from 'react-relay/compat';

import { AttributeTable, AttributeTableRow } from 'app/components/shared/AttributeTable';
import Emojify from 'app/components/shared/Emojify';

import AgentStore from 'app/stores/AgentStore';

import JobEvent from '../JobEvent';

import type { JobEventCreated_event } from './__generated__/JobEventCreated_job.graphql';

type Props = {
  job: JobEventCreated_event,
  previousTimestamp: ?string
};

function TableValue({ children }: { children: React.Node }) {
  return <code className="block dark-gray monospace">{ children }</code>;
}

function TableEmptyValue() {
  return <TableValue>-</TableValue>;
}

function agentQueryRulesWithoutQueue(queries) {
  return queries.filter((query) => !query.includes('queue='));
}

function clusterUrl(cluster) {
  return `/organizations/${cluster.organization.slug}/clusters/${cluster.uuid}`;
}

function clusterQueueUrl(clusterQueue) {
  const cluster = clusterQueue.cluster;

  return `/organizations/${cluster.organization.slug}/clusters/${cluster.uuid}/queues/${clusterQueue.uuid}`;
}

function JobEventCreated({ job, previousTimestamp }: Props) {
  const normalizedQuery = AgentStore.normalizedQuery(job.agentQueryRules);
  const agentQueryRules = job.cluster ? agentQueryRulesWithoutQueue(normalizedQuery) : normalizedQuery;

  return (
    <JobEvent timestamp={job.createdAt} previousTimestamp={previousTimestamp}>
      <JobEvent.Label>
        Job Created
      </JobEvent.Label>
      <JobEvent.Content>
        <AttributeTable>
          {job.cluster ? (
            <AttributeTableRow title="Cluster">
              <TableValue>
                <a href={clusterUrl(job.cluster)}>
                  <Emojify text={job.cluster.name} />
                </a>
              </TableValue>
            </AttributeTableRow>
          ) : null }
          {job.cluster ? (
            <AttributeTableRow title="Queue">
              <TableValue><a href={clusterQueueUrl(job.clusterQueue)}>{job.clusterQueue.key}</a></TableValue>
            </AttributeTableRow>
          ) : null }
          <AttributeTableRow title="Agent Targeting Rules">
            {agentQueryRules.length === 0
              ? <TableEmptyValue />
              : agentQueryRules.map((query) => <TableValue key={query}>{query}</TableValue>)}
          </AttributeTableRow>
          {job.concurrency ? (
            <React.Fragment>
              <AttributeTableRow title="Concurrency Group">
                <TableValue>{job.concurrency.group}</TableValue>
              </AttributeTableRow>
              <AttributeTableRow title="Concurrency Limit">
                <TableValue>{job.concurrency.limit}</TableValue>
              </AttributeTableRow>
            </React.Fragment>
          ) : null}
          {job.dependencies && (
            <AttributeTableRow title="Dependencies">
              {job.dependencies.map((dep) => <div key="dep">{dep}</div>)}
            </AttributeTableRow>
          )}
          {job.step?.key ? (
            <AttributeTableRow title="Step Key">
              <TableValue title={job.step.uuid}>{job.step.key}</TableValue>
            </AttributeTableRow>
          ) : null }
          {job.step?.dependencies.edges.length ? (
            <AttributeTableRow title="Step Dependencies">
              {job.step.dependencies.edges.map(({ node }) => (
                <div key={node.uuid}>
                  <TableValue>{node.key}</TableValue>
                </div>
              ))}
            </AttributeTableRow>
          ) : null }
          {job.step?.conditional ? (
            <AttributeTableRow title="Step Conditional">
              <TableValue>{job.step.conditional}</TableValue>
            </AttributeTableRow>
          ) : null }
        </AttributeTable>
      </JobEvent.Content>
    </JobEvent>
  );
}

export default createFragmentContainer(JobEventCreated, {
  job: graphql`
    fragment JobEventCreated_job on JobTypeCommand {
      ... on JobTypeCommand {
        uuid
        createdAt
        agentQueryRules
        cluster {
          name
          uuid
          organization {
            slug
          }
        }
        clusterQueue {
          key
          uuid
          cluster {
            name
            uuid
            organization {
              slug
            }
          }
        }
        concurrency {
          group
          limit
        }
        step {
          uuid
          key
          conditional
          dependencies(first: 10) {
            edges {
              node {
                uuid
                key
              }
            }
          }
        }
      }
    }
  `
});
